import clsx from 'clsx';
import { Activity, BrainCircuit, Download, FolderX, Settings, Table2, Upload } from 'lucide-react';
import { Suspense } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import { useProject } from '@/api/hooks/projects';
import { isApiError } from '@/api/client';
import { Badge, EmptyState, ErrorFallback, LoadingBlock } from '@/components';
import { ROLE_LABELS, roleAtLeast } from '@/lib/roles';
import { paths, useProjectId } from '@/routing';
import type { ProjectRole } from '@/api/types';
import styles from './ProjectLayout.module.css';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Table2;
  end?: boolean;
  minRole?: ProjectRole;
}

function navItems(pid: number): NavItem[] {
  return [
    { to: paths.project(pid), label: 'Data', icon: Table2, end: true },
    { to: paths.upload(pid), label: 'Upload', icon: Upload, minRole: 'annotator' },
    { to: paths.exports(pid), label: 'Exports', icon: Download },
    { to: paths.projectJobs(pid), label: 'Jobs', icon: Activity },
    { to: paths.models(pid), label: 'Models', icon: BrainCircuit },
    { to: `/projects/${pid}/settings`, label: 'Settings', icon: Settings },
  ];
}

/**
 * `/projects/:pid/*` frame: loads the project (children render only once it is loaded,
 * so pages can use `useCurrentProject()`), left navigation, scrollable content area.
 */
export function ProjectLayout() {
  const pid = useProjectId();
  const project = useProject(pid);

  if (project.isPending) return <LoadingBlock label="Loading project…" />;
  if (project.isError) {
    const missing =
      isApiError(project.error) && (project.error.status === 404 || project.error.status === 403);
    return (
      <main id="main" className={styles.content}>
        {missing ? (
          <EmptyState
            icon={<FolderX />}
            title="Project not found"
            description="It does not exist or you are not a member of it."
            action={<Link to={paths.home()}>Back to projects</Link>}
          />
        ) : (
          <ErrorFallback error={project.error} onRetry={() => void project.refetch()} />
        )}
      </main>
    );
  }

  const { data } = project;
  return (
    <div className={styles.layout}>
      <nav className={styles.nav} aria-label="Project">
        <div className={styles.projectHead}>
          <Link to={paths.project(pid)} className={clsx(styles.projectName, 'truncate')} title={data.name}>
            {data.name}
          </Link>
          <Badge tone={data.role === 'manager' ? 'accent' : 'neutral'}>{ROLE_LABELS[data.role]}</Badge>
        </div>
        <ul className={styles.navList}>
          {navItems(pid)
            .filter((item) => !item.minRole || roleAtLeast(data.role, item.minRole))
            .map(({ to, label, icon: Icon, end }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  title={label}
                  className={({ isActive }) => clsx(styles.navLink, isActive && styles.active)}
                >
                  <Icon aria-hidden />
                  <span>{label}</span>
                </NavLink>
              </li>
            ))}
        </ul>
      </nav>
      <main id="main" className={styles.content}>
        <Suspense fallback={<LoadingBlock />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
